import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetOverlay,
  SheetTitle,
} from '@parrot-software/design-system';
import { Download, RefreshCw } from '@parrot-software/design-system/icons';
import type { PaymentHistoryViewDto } from '@subscriptions/application/dtos';
import { PaymentStatusBadge } from '@subscriptions/presentation/components/base';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';
import {
  displayPaymentValue,
  getPaymentMethodLabel,
  handleDownload,
  resolvePaymentInvoiceActions,
} from '@subscriptions/presentation/utils';
import { PaymentSummaryCard } from './PaymentSummaryCard';

interface PaymentDetailSheetProps {
  blockUuid?: string;
  payment: PaymentHistoryViewDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Nesting level used to stack a child sheet above its parent. Top-level sheet is 0. */
  depth?: number;
}

/** Base z-index for the deepest (top-level) overlay; each nested level adds this step. */
const SHEET_Z_BASE = 1300;
const SHEET_Z_STEP = 60;

/**
 * Renders a label-value row for the payment detail info card.
 *
 * Accepts a ReactNode as value so callers can inject components (e.g. PaymentStatusBadge)
 * while keeping the text-based rows short.
 */
const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right">{value}</span>
  </div>
);

/**
 * PaymentDetailSheet - Bottom sheet showing the full detail of a single payment.
 *
 * Mobile-only counterpart of the desktop table row. Renders nothing when `payment` is null
 * so the parent can keep a single mounted instance and toggle it via `open`.
 *
 * For grouped payments (those carrying `relatedInvoices`), each child is shown below the
 * general info as a {@link PaymentSummaryCard}. Tapping a child opens a second, stacked
 * instance of this same sheet (via `depth`); closing it returns to the parent detail with
 * its children still visible.
 *
 * @param {PaymentDetailSheetProps} props - Component properties
 * @returns {React.ReactElement | null} Sheet with the payment detail, or null when no payment is selected
 */
export const PaymentDetailSheet = ({ blockUuid, payment, open, onOpenChange, depth = 0 }: PaymentDetailSheetProps) => {
  const { t } = useTranslation('paymentHistory');
  const [selectedChild, setSelectedChild] = useState<PaymentHistoryViewDto | null>(null);
  const areActionsLocked = useAreSubscriptionActionsLocked();

  const children = payment?.relatedInvoices ?? [];
  const hasChildren = children.length > 0;
  // Una hoja anidada muestra el CFDI de una sucursal, así que `depth` es lo que marca la fila hija.
  const { canDownload, retryPath } = payment
    ? resolvePaymentInvoiceActions({ blockUuid, isChild: depth > 0, payment })
    : { canDownload: false, retryPath: null };

  const overlayZIndex = SHEET_Z_BASE + depth * SHEET_Z_STEP;
  const contentZIndex = overlayZIndex + SHEET_Z_STEP / 2;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setSelectedChild(null);
    onOpenChange(nextOpen);
  };

  return (
    <>
      <Sheet onOpenChange={handleOpenChange} open={open}>
        <SheetOverlay className="bg-black/50" style={{ zIndex: overlayZIndex }} />
        <SheetContent
          className="w-full max-w-full flex flex-col gap-4 px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] font-sans max-h-[90dvh] overflow-y-auto rounded-t-2xl"
          side="bottom"
          style={{ zIndex: contentZIndex }}
        >
          {payment && (
            <>
              <SheetHeader className="text-left gap-1 items-center justify-center mb-2">
                <SheetTitle className="text-xl font-bold">{displayPaymentValue(payment.subscriptionAmount)}</SheetTitle>
                <p className="text-base text-muted-foreground mt-0!">{displayPaymentValue(payment.paymentDate)}</p>
              </SheetHeader>

              <Card>
                <CardContent className="p-4">
                  <p className="text-base font-semibold pb-2">{t('detail.generalInfo')}</p>
                  <div className="flex flex-col">
                    <DetailRow label={t('columns.subscription')} value={displayPaymentValue(payment.subscription)} />
                    <DetailRow label={t('columns.concept')} value={displayPaymentValue(payment.concept)} />
                    <DetailRow label={t('columns.status')} value={<PaymentStatusBadge status={payment.status} />} />
                    <DetailRow
                      label={t('columns.paymentMethod')}
                      value={displayPaymentValue(
                        getPaymentMethodLabel(payment.paymentMethod, payment.paymentMethodDetail, t),
                      )}
                    />
                    <DetailRow
                      label={t('columns.subscriptionAmount')}
                      value={displayPaymentValue(payment.subscriptionAmount)}
                    />
                  </div>
                </CardContent>
              </Card>

              {canDownload && (
                <Button
                  className="w-full"
                  onClick={() => {
                    void handleDownload(payment.pdfUrl, payment.xmlUrl, payment.folio, {
                      errorMessage: t('downloadError'),
                      successMessage: t('downloadSuccess'),
                      successDescription: t('downloadSuccessDescription'),
                    });
                  }}
                  variant="outline"
                >
                  <Download className="mr-1 h-4 w-4" />
                  {t('downloadPdfXml')}
                </Button>
              )}

              {retryPath && areActionsLocked && (
                <Button className="w-full" disabled variant="outline">
                  <RefreshCw className="mr-1 h-4 w-4" />
                  {t('retryInvoice')}
                </Button>
              )}

              {retryPath && !areActionsLocked && (
                <Button asChild className="w-full" variant="outline">
                  <Link data-testid="payment-detail-retry-link" to={retryPath}>
                    <RefreshCw className="mr-1 h-4 w-4" />
                    {t('retryInvoice')}
                  </Link>
                </Button>
              )}

              {hasChildren && (
                <div className="flex flex-col gap-3">
                  {children.map((child) => (
                    <PaymentSummaryCard key={child.uuid} onClick={setSelectedChild} payment={child} />
                  ))}
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {hasChildren && (
        <PaymentDetailSheet
          blockUuid={blockUuid}
          depth={depth + 1}
          onOpenChange={(childOpen) => {
            if (!childOpen) setSelectedChild(null);
          }}
          open={Boolean(selectedChild)}
          payment={selectedChild}
        />
      )}
    </>
  );
};
